import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Clock, MapPin, User, Phone, CheckCircle, CreditCard } from 'lucide-react';
import { useEmailNotifications } from '@/hooks/useEmailNotifications';
import { useOrderNotifications } from '@/hooks/useOrderNotifications';

interface Order {
  id: string;
  customer_id: string;
  delivery_address: string;
  delivery_type: string;
  total_amount: number;
  delivery_fee: number;
  status: string;
  created_at: string;
  customer_name: string | null;
  customer_phone: string | null;
  notes: string | null;
  payment_method: string | null;
  payment_status: string | null;
  driver_id: string | null;
  order_items: {
    quantity: number;
    price: number;
    name: string | null;
    customizations?: any;
    menu_items: {
      name: string;
    } | null;
  }[];
  restaurants?: {
    name: string;
    address: string;
  };
}

interface ActiveOrdersProps {
  restaurantId: string;
}

const ActiveOrders = ({ restaurantId }: ActiveOrdersProps) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [customizationDetails, setCustomizationDetails] = useState<Map<string, any>>(new Map());
  const { toast } = useToast();
  const { sendNotification } = useEmailNotifications();
  const { sendOrderNotification, sendDriverNotification, sendVendorNotification } = useOrderNotifications();

  useEffect(() => {
    fetchActiveOrders();
    
    const channel = supabase
      .channel('active-orders')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `restaurant_id=eq.${restaurantId}`
        },
        (payload) => {
          console.log('Order update received:', payload);
          fetchActiveOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [restaurantId]);

  const fetchActiveOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            quantity,
            price,
            name,
            customizations,
            menu_items (
              name
            )
          ),
          restaurants (
            name,
            address
          )
        `)
        .eq('restaurant_id', restaurantId)
        .in('status', ['pending', 'confirmed', 'ready_for_pickup', 'picked_up'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching orders:', error);
      } else {
        console.log('Fetched active orders:', data);
        setOrders(data || []);
        
        // Fetch customization details for all orders
        if (data && data.length > 0) {
          await fetchCustomizationDetails(data);
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomizationDetails = async (orders: Order[]) => {
    const customizationMap = new Map();

    for (const order of orders) {
      for (const item of order.order_items || []) {
        if (item.customizations && item.customizations.length > 0) {
          for (const customization of item.customizations) {
            const customizationId = customization.customization_id;
            
            if (!customizationMap.has(customizationId)) {
              try {
                // Fetch customization details
                const { data: customizationData } = await supabase
                  .from('menu_item_customizations')
                  .select(`
                    id,
                    name,
                    type,
                    customization_options (
                      id,
                      name,
                      price_amount
                    )
                  `)
                  .eq('id', customizationId)
                  .single();

                if (customizationData) {
                  customizationMap.set(customizationId, customizationData);
                }
              } catch (error) {
                console.error('Error fetching customization details:', error);
              }
            }
          }
        }
      }
    }

    setCustomizationDetails(customizationMap);
  };

  const findBestAvailableDriver = async () => {
    try {
      // Get all active drivers with their current workload
      const { data: driversWithWorkload, error } = await supabase
        .from('driver_profiles')
        .select(`
          user_id,
          is_active,
          current_latitude,
          current_longitude,
          last_location_update
        `)
        .eq('is_active', true)
        .not('current_latitude', 'is', null)
        .not('current_longitude', 'is', null)
        .gte('last_location_update', new Date(Date.now() - 10 * 60 * 1000).toISOString());

      if (error || !driversWithWorkload?.length) {
        console.log('No available drivers found');
        return null;
      }

      // Get current order counts for each driver
      const driverIds = driversWithWorkload.map(d => d.user_id);
      const { data: orderCounts } = await supabase
        .from('orders')
        .select('driver_id')
        .in('driver_id', driverIds)
        .in('status', ['confirmed', 'ready_for_pickup', 'picked_up']);

      // Calculate workload for each driver
      const workloadMap = new Map();
      orderCounts?.forEach(order => {
        const driverId = order.driver_id;
        workloadMap.set(driverId, (workloadMap.get(driverId) || 0) + 1);
      });

      // Find driver with no current orders (only assign to completely free drivers)
      let bestDriver = null;

      for (const driver of driversWithWorkload) {
        const currentWorkload = workloadMap.get(driver.user_id) || 0;
        
        // Only assign to drivers with no current orders
        if (currentWorkload === 0) {
          bestDriver = driver.user_id;
          break;
        }
      }

      // If no free driver found, return null (don't assign the order)
      if (!bestDriver) {
        console.log('No available drivers with zero workload found');
        return null;
      }

      return bestDriver;
    } catch (error) {
      console.error('Error finding available driver:', error);
      return null;
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: 'confirmed' | 'ready_for_pickup' | 'picked_up' | 'cancelled') => {
    if (updatingOrders.has(orderId)) {
      return;
    }

    setUpdatingOrders(prev => new Set([...prev, orderId]));

    try {
      console.log(`Updating order ${orderId} to status: ${newStatus}`);
      
      const order = orders.find(o => o.id === orderId);
      
      // If confirming a delivery order, assign to best available driver
      if (newStatus === 'confirmed' && order?.delivery_type === 'delivery') {
        const bestDriverId = await findBestAvailableDriver();
        
        if (bestDriverId) {
          // Update order with status and driver assignment
          const { error } = await supabase
            .from('orders')
            .update({ 
              status: newStatus,
              driver_id: bestDriverId,
              updated_at: new Date().toISOString()
            })
            .eq('id', orderId);

          if (error) {
            console.error('Update error:', error);
            throw new Error(`Failed to update order: ${error.message}`);
          }

          console.log(`Order ${orderId} assigned to driver ${bestDriverId}`);
        } else {
          // No driver available, just update status (will be assigned when driver comes online)
          const { error } = await supabase
            .from('orders')
            .update({ 
              status: newStatus,
              updated_at: new Date().toISOString()
            })
            .eq('id', orderId);

          if (error) {
            console.error('Update error:', error);
            throw new Error(`Failed to update order: ${error.message}`);
          }

          console.log(`Order ${orderId} confirmed but no available drivers - will be assigned when driver comes online`);
        }
      } else {
        // For non-delivery orders or other status updates
        const { error } = await supabase
          .from('orders')
          .update({ 
            status: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', orderId);

        if (error) {
          console.error('Update error:', error);
          throw new Error(`Failed to update order: ${error.message}`);
        }
      }

      console.log('Order updated successfully');

      // Send push notifications
      if (order) {
        // Send notification to customer
        await sendOrderNotification(orderId, newStatus, order.customer_name || undefined);

        // Send notification to driver if a driver was assigned during confirmation
        if (newStatus === 'confirmed' && order.delivery_type === 'delivery') {
          // Check if we just assigned a driver (get the updated order info)
          const { data: updatedOrder } = await supabase
            .from('orders')
            .select('driver_id')
            .eq('id', orderId)
            .single();
          
          if (updatedOrder?.driver_id) {
            await sendDriverNotification(updatedOrder.driver_id, {
              orderId: order.id,
              restaurantName: order.restaurants?.name || 'Restaurant',
              customerName: order.customer_name || 'Customer',
              deliveryAddress: order.delivery_address
            });
          }
        }
      }

      // Send email notification to customer
      if (order) {
        const customerEmail = await getCustomerEmail(order.customer_id);
        if (customerEmail) {
          await sendNotification(
            'order_status_update',
            orderId,
            customerEmail,
            order.customer_name || 'Customer',
            {
              status: newStatus,
              total: order.total_amount
            }
          );
        }
      }

      let successMessage = '';
      if (newStatus === 'confirmed') {
        if (order?.delivery_type === 'delivery') {
          successMessage = 'Order accepted and assigned to available driver';
        } else {
          successMessage = 'Order accepted successfully';
        }
      } else if (newStatus === 'ready_for_pickup') {
        successMessage = 'Order ready for pickup!';
      } else if (newStatus === 'picked_up') {
        successMessage = 'Order marked as picked up!';
      } else if (newStatus === 'cancelled') {
        successMessage = 'Order cancelled';
      }

      toast({
        title: "Order Updated", 
        description: successMessage,
      });

      await fetchActiveOrders();
    } catch (error: any) {
      console.error('Error updating order status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update order status",
        variant: "destructive",
      });
    } finally {
      setUpdatingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const getCustomerEmail = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', customerId)
        .single();
      
      if (error || !data) return null;
      return data.email;
    } catch (error) {
      console.error('Error getting customer email:', error);
      return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'ready_for_pickup':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionButton = (order: Order) => {
    const isUpdating = updatingOrders.has(order.id);
    
    switch (order.status) {
      case 'pending':
        return (
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => updateOrderStatus(order.id, 'confirmed')}
              className="bg-green-600 hover:bg-green-700"
              disabled={isUpdating}
            >
              {isUpdating ? 'Accepting...' : 'Accept'}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => updateOrderStatus(order.id, 'cancelled')}
              disabled={isUpdating}
            >
              {isUpdating ? 'Declining...' : 'Decline'}
            </Button>
          </div>
        );
      case 'confirmed':
        return (
          <Button
            size="sm"
            onClick={() => updateOrderStatus(order.id, 'ready_for_pickup')}
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            disabled={isUpdating}
          >
            <CheckCircle className="h-4 w-4" />
            {isUpdating ? 'Updating...' : (order.delivery_type === 'pickup' ? 'Ready for Customer Pickup' : 'Ready for Driver Pickup')}
          </Button>
        );
      case 'ready_for_pickup':
        if (order.delivery_type === 'pickup') {
          return (
            <Button
              size="sm"
              onClick={() => updateOrderStatus(order.id, 'picked_up')}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
              disabled={isUpdating}
            >
              <CheckCircle className="h-4 w-4" />
              {isUpdating ? 'Updating...' : 'Mark as Picked Up'}
            </Button>
          );
        }
        return (
          <div className="text-sm text-blue-600 font-medium">
            {order.driver_id ? 'Assigned to driver' : 'Waiting for driver assignment'}
          </div>
        );
      default:
        return null;
    }
  };

  const getStatusDescription = (order: Order) => {
    switch (order.status) {
      case 'pending':
        return 'Waiting for your confirmation';
      case 'confirmed':
        if (order.delivery_type === 'delivery') {
          return order.driver_id ? 'Driver assigned - preparing order...' : 'Will be assigned to next available driver';
        }
        return 'Preparing order...';
      case 'ready_for_pickup':
        if (order.delivery_type === 'pickup') {
          return 'Ready for customer pickup';
        } else {
          return 'Ready for driver pickup';
        }
      default:
        return '';
    }
  };

  const getVendorTotal = (order: Order) => {
    return order.total_amount - order.delivery_fee;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Active Orders</h3>
          <p className="text-gray-600">New orders will appear here when customers place them.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {orders.map((order) => (
        <Card key={order.id}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Order #{order.id.slice(0, 8)}</CardTitle>
              <div className="flex items-center gap-2">
                <Badge className={getStatusColor(order.status)}>
                  {order.status.replace('_', ' ').toUpperCase()}
                </Badge>
                {order.delivery_type === 'pickup' && (
                  <Badge variant="outline" className="text-xs">
                    PICKUP
                  </Badge>
                )}
                {order.payment_method === 'wave' && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                    <CreditCard className="h-3 w-3 mr-1" />
                    WAVE
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{new Date(order.created_at).toLocaleTimeString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <span>{order.customer_name || 'Anonymous'}</span>
              </div>
              {order.customer_phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  <span>{order.customer_phone}</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500 italic">{getStatusDescription(order)}</p>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Items:</h4>
              <div className="space-y-3">
                {order.order_items?.map((item, index) => (
                  <div key={index} className="border-l-2 border-gray-200 pl-3">
                    <div className="flex justify-between text-sm font-medium">
                      <span>{item.quantity}x {item.menu_items?.name || item.name || 'Unknown Item'}</span>
                      <span>D{item.price.toFixed(2)}</span>
                    </div>
                    {item.customizations && item.customizations.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-600 font-medium">Customizations:</p>
                        {item.customizations.map((customization: any, custIndex: number) => {
                          const details = customizationDetails.get(customization.customization_id);
                          
                          return (
                            <div key={custIndex} className="text-xs text-gray-600 ml-2">
                              {details && (
                                <div>
                                  <span className="font-medium">{details.name}:</span>
                                  
                                  {/* Display text input */}
                                  {details.type === 'text_input' && customization.text_input && (
                                    <span className="ml-1">"{customization.text_input}"</span>
                                  )}
                                  
                                  {/* Display selected options */}
                                  {customization.selected_options && customization.selected_options.length > 0 && (
                                    <span className="ml-1">
                                      {customization.selected_options.map((optionId: string) => {
                                        const option = details.customization_options?.find((opt: any) => opt.id === optionId);
                                        return option?.name;
                                      }).filter(Boolean).join(', ')}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Fallback if no details found */}
                              {!details && (
                                <div>
                                  {customization.text_input && (
                                    <span>Custom text: "{customization.text_input}"</span>
                                  )}
                                  {customization.selected_options && customization.selected_options.length > 0 && (
                                    <span>Selected: {customization.selected_options.length} option(s)</span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {order.delivery_type === 'pickup' ? 'Pickup Order' : 'Delivery Address:'}
                </p>
                {order.delivery_type === 'delivery' && (
                  <p className="text-sm text-gray-600">{order.delivery_address}</p>
                )}
                {order.delivery_type === 'pickup' && (
                  <p className="text-sm text-gray-600">Customer will pick up from restaurant</p>
                )}
              </div>
            </div>

            {order.notes && (
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm font-medium mb-1">Customer Notes:</p>
                <p className="text-sm text-gray-600">{order.notes}</p>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t">
              <div className="text-lg font-semibold">
                Your Total: D{getVendorTotal(order).toFixed(2)}
              </div>
              {getActionButton(order)}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ActiveOrders;
